<script setup lang="ts">
export interface LeaderboardAchievement {
  name: string
  icon: string
  iconColor: string | null
}

export interface LeaderboardEntry {
  rank: number
  characterId: string
  username: string
  displayName: string | null
  avatarUrl: string | null
  achievements: LeaderboardAchievement[]
  views: number
  clicks: number
  clipViews: number
  setupClicks: number
}

export type LeaderboardMetric = 'views' | 'clicks' | 'clipViews' | 'setupClicks'

const props = withDefaults(defineProps<{
  entries: LeaderboardEntry[]
  loading?: boolean
  error?: boolean
  metric?: LeaderboardMetric
  highlightCharacterId?: string | null
  compact?: boolean
  /** Hide the metric count (homepage teaser: small numbers read as "nobody here") */
  hideCount?: boolean
}>(), {
  loading: false,
  error: false,
  metric: 'views',
  highlightCharacterId: null,
  compact: false,
  hideCount: false
})

const RANK_COLORS: Record<number, string> = {
  1: 'text-amber-500',
  2: 'text-slate-400',
  3: 'text-orange-500'
}

const METRIC_ICONS: Record<LeaderboardMetric, string> = {
  views: 'i-lucide-eye',
  clicks: 'i-lucide-mouse-pointer-click',
  clipViews: 'i-lucide-play',
  setupClicks: 'i-lucide-monitor'
}

// Skeletons only on the very first load; when switching filters the previous
// entries stay visible (dimmed) and animate to their new order
const showSkeletons = computed(() => props.loading && props.entries.length === 0)

function formatCount(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
  if (count >= 1_000) return `${(count / 1_000).toFixed(1).replace(/\.0$/, '')}K`
  return count.toString()
}
</script>

<template>
  <div class="w-full overflow-hidden rounded-2xl border border-default bg-elevated/40 shadow-(--surface-shadow)">
    <!-- Loading skeletons (initial load only) -->
    <div
      v-if="showSkeletons"
      class="flex flex-col divide-y divide-default"
    >
      <div
        v-for="i in (compact ? 3 : 10)"
        :key="i"
        class="flex items-center gap-3 px-4 sm:px-5 py-3"
      >
        <USkeleton class="h-4 w-5 shrink-0" />
        <USkeleton class="size-10 rounded-full shrink-0" />
        <USkeleton class="h-4 w-32" />
        <USkeleton class="h-4 w-14 ml-auto" />
      </div>
    </div>

    <!-- Error state -->
    <div
      v-else-if="error"
      class="flex flex-col items-center gap-2 py-12 text-center"
    >
      <UIcon
        name="i-lucide-cloud-off"
        class="size-8 text-dimmed"
      />
      <p class="text-sm text-muted">
        Couldn't load the leaderboard. Please try again later.
      </p>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="entries.length === 0"
      class="flex flex-col items-center gap-2 py-12 text-center"
    >
      <UIcon
        name="i-lucide-trophy"
        class="size-8 text-dimmed"
      />
      <p class="text-sm text-muted">
        No entries yet. Be the first on the board.
      </p>
    </div>

    <!-- Entries -->
    <TransitionGroup
      v-else
      tag="div"
      name="board"
      class="relative flex flex-col divide-y divide-default transition-opacity duration-300"
      :class="loading ? 'opacity-50 pointer-events-none' : 'opacity-100'"
    >
      <NuxtLink
        v-for="entry in entries"
        :key="entry.characterId"
        :to="`/${entry.username}`"
        target="_blank"
        class="group flex items-center gap-3 px-4 sm:px-5 py-3 transition-colors duration-200"
        :class="entry.characterId === highlightCharacterId
          ? 'bg-primary-500/8 hover:bg-primary-500/12'
          : 'hover:bg-elevated/60'"
      >
        <!-- Rank -->
        <span
          class="w-6 shrink-0 text-center text-sm font-semibold tabular-nums"
          :class="RANK_COLORS[entry.rank] || 'text-dimmed'"
        >
          {{ entry.rank }}
        </span>

        <!-- Avatar -->
        <UAvatar
          :src="entry.avatarUrl || undefined"
          :alt="entry.displayName || entry.username"
          :text="(entry.displayName || entry.username).charAt(0).toUpperCase()"
          size="md"
          class="shrink-0"
        />

        <!-- Name + achievements -->
        <div class="flex min-w-0 flex-col">
          <div class="flex min-w-0 items-center gap-1.5">
            <span class="truncate text-sm font-semibold text-highlighted transition-colors group-hover:text-primary-400">
              {{ entry.displayName || entry.username }}
            </span>
            <div
              v-if="entry.achievements.length > 0"
              class="hidden shrink-0 items-center gap-1 sm:flex"
            >
              <UTooltip
                v-for="achievement in entry.achievements"
                :key="achievement.name"
                :text="achievement.name"
                :delay-duration="300"
              >
                <UIcon
                  :name="achievement.icon"
                  class="size-3.5 text-muted"
                  :style="achievement.iconColor ? { color: achievement.iconColor } : undefined"
                />
              </UTooltip>
            </div>
          </div>
          <span class="truncate text-xs text-muted">
            mychar.gg/{{ entry.username }}
          </span>
        </div>

        <!-- Metric count -->
        <div
          v-if="!hideCount"
          class="ml-auto flex shrink-0 items-center gap-1.5 text-toned"
        >
          <UIcon
            :name="METRIC_ICONS[metric]"
            class="size-4"
          />
          <span class="text-sm font-semibold tabular-nums">
            {{ formatCount(entry[metric]) }}
          </span>
        </div>
      </NuxtLink>
    </TransitionGroup>
  </div>
</template>

<style scoped>
.board-move,
.board-enter-active,
.board-leave-active {
  transition: all 0.35s ease;
}

.board-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.board-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Leaving rows are taken out of the flow so remaining rows slide smoothly */
.board-leave-active {
  position: absolute;
  width: 100%;
}
</style>
